import { Html } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { easing } from "maath";
import { useMemo, useRef, useState } from "react";
import { AdditiveBlending, Color, DoubleSide, type Group, type ShaderMaterial } from "three";
import type { WorldLook } from "../engine/types";
import type { Planet } from "./planets";
import { ATMOS_FRAG, CLOUD_FRAG, PLANET_FRAG, PLANET_VERT } from "./shaders";

function hexToVec(hex: string) {
  const c = new Color(hex);
  return [c.r, c.g, c.b] as [number, number, number];
}

function SurfaceDust({ radius, color }: { radius: number; color: string }) {
  const positions = useMemo(() => {
    const n = 160;
    const arr = new Float32Array(n * 3);
    const phi = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < n; i++) {
      const y = 1 - (i / (n - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = phi * i;
      const jitter = 1.02 + (i % 7) * 0.008;
      arr[i * 3] = Math.cos(theta) * r * radius * jitter;
      arr[i * 3 + 1] = y * radius * jitter;
      arr[i * 3 + 2] = Math.sin(theta) * r * radius * jitter;
    }
    return arr;
  }, [radius]);
  return (
    <points raycast={() => null}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.045}
        color={color}
        transparent
        opacity={0.7}
        depthWrite={false}
        blending={AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

export function lookFromPlanet(planet: Planet, scale = 1): WorldLook {
  return {
    colorA: planet.colorA,
    colorB: planet.colorB,
    atmosphere: planet.atmosphere,
    seed: planet.seed,
    radius: planet.radius * scale,
    ring: planet.ring,
    habitable: planet.habitable,
  };
}

export function WorldPlanet({
  look,
  label,
  eyebrow,
  faded = false,
  growing = false,
  spawnAt = 0,
  detail = 48,
  dim = false,
  focused = false,
  stagger = 0,
  showLabel = true,
  onClick,
}: {
  look: WorldLook;
  label: string;
  eyebrow?: string;
  faded?: boolean;
  growing?: boolean;
  spawnAt?: number;
  detail?: number;
  dim?: boolean;
  focused?: boolean;
  stagger?: number;
  showLabel?: boolean;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const group = useRef<Group>(null);
  const body = useRef<Group>(null);
  const showFull = focused || hovered;
  const mat = useRef<ShaderMaterial>(null);
  const cloud = useRef<ShaderMaterial>(null);
  const scaleRef = useRef(0.08);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uSeed: { value: look.seed },
      uColorA: { value: hexToVec(look.colorA) },
      uColorB: { value: hexToVec(look.colorB) },
      uAtmosphere: { value: hexToVec(look.atmosphere) },
      uHabitable: { value: look.habitable ? 1 : 0 },
      uColor: { value: hexToVec(look.atmosphere) },
    }),
    [look],
  );

  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    if (mat.current) mat.current.uniforms.uTime.value = t;
    if (cloud.current) cloud.current.uniforms.uTime.value = t;
    if (!group.current) return;
    group.current.rotation.y = t * 0.05;
    const ready = performance.now() - spawnAt > stagger;
    const grow = !growing || !ready ? (growing ? 0 : 1) : Math.min(1, Math.max(0, (performance.now() - spawnAt - stagger) / 780));
    const breath = focused ? 1 + Math.sin(t * 2) * 0.025 : 1;
    const target =
      (0.55 + grow * 0.45) * (faded ? 0.7 : dim ? 0.85 : 1) * (focused ? 1.12 : 1) * breath;
    easing.damp(scaleRef, "current", target, focused ? 0.18 : 0.28, delta);
    group.current.position.set(0, 0, 0);
    if (body.current) body.current.scale.setScalar(scaleRef.current);
  });

  const segs = Math.max(16, Math.round(detail));
  return (
    <group ref={group}>
      <mesh>
        <sphereGeometry args={[look.radius * 0.42, 16, 12]} />
        <meshBasicMaterial color={look.atmosphere} />
      </mesh>
      <group ref={body}>
      <mesh
        onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = "auto";
        }}
      >
        <sphereGeometry args={[look.radius, segs, Math.round(segs * 0.75)]} />
        <shaderMaterial ref={mat} vertexShader={PLANET_VERT} fragmentShader={PLANET_FRAG} uniforms={uniforms} />
      </mesh>
      {look.habitable && (
        <mesh scale={1.02}>
          <sphereGeometry args={[look.radius, 24, 18]} />
          <shaderMaterial
            ref={cloud}
            vertexShader={PLANET_VERT}
            fragmentShader={CLOUD_FRAG}
            uniforms={uniforms}
            transparent
            depthWrite={false}
          />
        </mesh>
      )}
      <mesh scale={1.14}>
        <sphereGeometry args={[look.radius, 24, 18]} />
        <shaderMaterial
          vertexShader={PLANET_VERT}
          fragmentShader={ATMOS_FRAG}
          uniforms={uniforms}
          transparent
          depthWrite={false}
          side={DoubleSide}
        />
      </mesh>
      <mesh scale={1.55}>
        <sphereGeometry args={[look.radius, 16, 12]} />
        <meshBasicMaterial color={look.atmosphere} transparent opacity={faded ? 0.08 : 0.22} depthWrite={false} blending={AdditiveBlending} />
      </mesh>
      <pointLight color={look.atmosphere} intensity={faded ? 0.4 : 8} distance={look.radius * 8} decay={2} />
      <SurfaceDust radius={look.radius} color={look.atmosphere} />
      {look.ring && (
        <mesh rotation={[Math.PI / 2.35, 0.18, 0]}>
          <torusGeometry args={[look.radius * 1.52, Math.max(0.012, look.radius * 0.03), 8, 64]} />
          <meshBasicMaterial color={look.atmosphere} transparent opacity={faded ? 0.12 : 0.4} />
        </mesh>
      )}
      </group>
      {showLabel && !faded && showFull && (
        <Html
          position={[0, -look.radius - 0.35, 0]}
          center
          distanceFactor={15}
          occlude
          style={{ pointerEvents: "none" }}
          zIndexRange={[10, 0]}
        >
          <div className="world-label world-label--open">
            <strong>{label}</strong>
          </div>
        </Html>
      )}
    </group>
  );
}
